import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, Megaphone, Play, Plus, RefreshCw, Save, Send, Trash2 } from "lucide-react";
import { api } from "../lib/api";

const blankPlug = {
  name: "",
  message: "",
  enabled: true,
};

const inputClass = "w-full h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))] disabled:opacity-50";
const textareaClass = "w-full min-h-[120px] px-3 py-2 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))] disabled:opacity-50";

const defaultManager = {
  enabled: false,
  interval_minutes: 15,
  min_chat_messages_between_plugs: 0,
  messages_since_last_sent: 0,
  only_when_live: true,
  last_status: "idle",
  last_error: "",
  last_sent_at: null,
};

function formFromPlug(plug) {
  if (!plug) return blankPlug;
  return {
    name: plug.name || "",
    message: plug.message || "",
    enabled: plug.enabled !== false,
  };
}

function payloadFromForm(form) {
  return {
    name: form.name,
    message: form.message,
    enabled: Boolean(form.enabled),
  };
}

export default function PlugManager() {
  const [plugs, setPlugs] = useState([]);
  const [currentPlug, setCurrentPlug] = useState(null);
  const [manager, setManager] = useState(defaultManager);
  const [history, setHistory] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [createForm, setCreateForm] = useState(blankPlug);
  const [editForm, setEditForm] = useState(blankPlug);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const selected = useMemo(
    () => plugs.find((plug) => plug.id === selectedId) || plugs[0] || null,
    [plugs, selectedId]
  );
  const enabledPlugs = useMemo(
    () => plugs.filter((plug) => plug.enabled !== false && String(plug.message || "").trim()),
    [plugs]
  );

  useEffect(() => {
    setEditForm(formFromPlug(selected));
  }, [selected]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/plugs");
      const next = data.plugs || [];
      setPlugs(next);
      setCurrentPlug(data.current || null);
      setManager(data.manager || defaultManager);
      setHistory(data.history || []);
      setSelectedId((current) => next.find((plug) => plug.id === current)?.id || next[0]?.id || "");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load plug messages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const replacePlug = (updated) => {
    setPlugs((current) => {
      const exists = current.some((plug) => plug.id === updated.id);
      return exists ? current.map((plug) => (plug.id === updated.id ? updated : plug)) : [updated, ...current];
    });
    setSelectedId(updated.id);
  };

  const runAction = async (label, action) => {
    setBusy(label);
    setError("");
    setMessage("");
    try {
      const updated = await action();
      if (updated) replacePlug(updated);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Plug action failed.");
    } finally {
      setBusy("");
    }
  };

  const createPlug = async (event) => {
    event.preventDefault();
    await runAction("create", async () => {
      const { data } = await api.post("/admin/plugs", payloadFromForm(createForm));
      setCreateForm(blankPlug);
      setMessage("Plug message created.");
      return data;
    });
  };

  const savePlug = async () => {
    if (!selected) return;
    await runAction("save", async () => {
      const { data } = await api.patch(`/admin/plugs/${selected.id}`, payloadFromForm(editForm));
      setMessage("Plug message saved.");
      return data;
    });
  };

  const deletePlug = async () => {
    if (!selected) return;
    const ok = window.confirm(`Delete "${selected.name}"?`);
    if (!ok) return;
    setBusy("delete");
    setError("");
    setMessage("");
    try {
      await api.delete(`/admin/plugs/${selected.id}`);
      setMessage("Plug message deleted.");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not delete plug message.");
    } finally {
      setBusy("");
    }
  };

  const saveManager = async () => {
    await runAction("settings", async () => {
      const { data } = await api.patch("/admin/plugs/settings", {
        enabled: Boolean(manager.enabled),
        interval_minutes: Number(manager.interval_minutes) || 15,
        min_chat_messages_between_plugs: Number(manager.min_chat_messages_between_plugs) || 0,
        only_when_live: Boolean(manager.only_when_live),
      });
      setManager(data.manager || manager);
      setMessage("Plug Manager settings saved.");
      return null;
    });
  };

  const sendNext = async () => {
    await runAction("send-next", async () => {
      await api.post("/admin/plugs/send-now", {});
      setMessage("Plug sent to chat.");
      return null;
    });
  };

  const sendSelected = async () => {
    if (!selected) return;
    await runAction("send-selected", async () => {
      await api.post("/admin/plugs/send-now", { plug_id: selected.id });
      setMessage("Selected plug sent to chat.");
      return null;
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-plugs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Rotating plugs</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[hsl(var(--primary))]" />
            Plug Manager
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
            Configure reusable Twitch chat messages. Enabled plugs are randomly rotated through on one shared timer.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading || !!busy}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {(error || message) && (
        <div className={`rounded border px-4 py-3 text-sm ${error ? "border-red-500/40 bg-red-500/10 text-red-100" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"}`}>
          {error || message}
        </div>
      )}

      <section className="surface p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow">Auto-send control</div>
            <h2 className="text-lg font-semibold text-white mt-1">Plug Manager is {manager.enabled ? "enabled" : "disabled"}</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Last status: {manager.last_status || "idle"}{manager.last_error ? ` - ${manager.last_error}` : ""}
            </p>
            <p className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mt-2">
              Chat messages since last plug: {manager.messages_since_last_sent || 0}
              {manager.min_chat_messages_between_plugs > 0 ? ` / ${manager.min_chat_messages_between_plugs}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={sendNext} disabled={!!busy} className="inline-flex items-center gap-2 rounded bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-medium disabled:opacity-50">
              <Send className="w-4 h-4" /> Send next
            </button>
            <button onClick={saveManager} disabled={!!busy} className="inline-flex items-center gap-2 rounded border border-[hsl(var(--border))] text-white px-4 py-2 text-sm hover:bg-[hsl(var(--surface-2))] disabled:opacity-50">
              <Save className="w-4 h-4" /> Save settings
            </button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_12rem_12rem] gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Toggle label="Auto-send enabled" checked={manager.enabled} onChange={(enabled) => setManager({ ...manager, enabled })} icon={Megaphone} />
            <Toggle label="Only while live" checked={manager.only_when_live} onChange={(only_when_live) => setManager({ ...manager, only_when_live })} icon={Eye} />
          </div>
          <Field label="Send every minutes">
            <input type="number" min="1" max="240" className={inputClass} value={manager.interval_minutes || 15} onChange={(event) => setManager({ ...manager, interval_minutes: event.target.value })} />
          </Field>
          <Field label="Minimum chat messages">
            <input type="number" min="0" max="1000" className={inputClass} value={manager.min_chat_messages_between_plugs || 0} onChange={(event) => setManager({ ...manager, min_chat_messages_between_plugs: event.target.value })} />
          </Field>
        </div>
      </section>

      <section className="surface p-5">
        <div className="label-eyebrow">Recent send status</div>
        {history.length ? (
          <div className="mt-3 divide-y divide-[hsl(var(--border))] rounded border border-[hsl(var(--border))] overflow-hidden">
            {history.map((entry) => (
              <div key={entry.id} className="p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 bg-black/20">
                <div>
                  <div className="font-medium text-white">{entry.plug_name}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{entry.message}</div>
                  {entry.error && <div className="text-xs text-red-200 mt-1">{entry.error}</div>}
                </div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
                  <Pill active={entry.status === "sent"}>{entry.status}</Pill>
                  <span>{entry.trigger}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">No plug sends recorded yet.</p>
        )}
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[22rem_1fr] gap-6">
        <aside className="space-y-4">
          <form onSubmit={createPlug} className="surface p-5 space-y-4">
            <div>
              <div className="label-eyebrow">New plug</div>
              <h2 className="text-lg font-semibold text-white mt-1">Add message</h2>
            </div>
            <PlugFields form={createForm} onChange={setCreateForm} compact />
            <button disabled={!!busy} className="w-full inline-flex items-center justify-center gap-2 rounded bg-[hsl(var(--primary))] text-white py-2 text-sm font-medium disabled:opacity-50">
              <Plus className="w-4 h-4" /> Create plug
            </button>
          </form>

          <div className="surface overflow-hidden">
            <div className="p-4 border-b border-[hsl(var(--border))]">
              <div className="label-eyebrow">Configured plugs</div>
            </div>
            {loading ? (
              <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">Loading plugs...</div>
            ) : plugs.length === 0 ? (
              <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">No plug messages yet.</div>
            ) : (
              <div className="divide-y divide-[hsl(var(--border))]">
                {plugs.map((plug) => (
                  <button
                    key={plug.id}
                    type="button"
                    onClick={() => setSelectedId(plug.id)}
                    className={`w-full text-left p-4 hover:bg-[hsl(var(--surface-2))] ${selected?.id === plug.id ? "bg-[hsl(var(--surface-2))]" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate">{plug.name}</div>
                      {plug.enabled ? <Pill active>Enabled</Pill> : <Pill>Disabled</Pill>}
                    </div>
                    <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))] line-clamp-2">{plug.message}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        <section className="surface p-5 space-y-5">
          {selected ? (
            <>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="label-eyebrow">Selected plug</div>
                  <h2 className="text-xl font-semibold text-white mt-1">{selected.name}</h2>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Edit the message, status, and rotation timing.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={savePlug} disabled={!!busy} className="inline-flex items-center gap-2 rounded bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-medium disabled:opacity-50">
                    <Save className="w-4 h-4" /> Save
                  </button>
                  <button onClick={sendSelected} disabled={!!busy || selected.enabled === false} className="inline-flex items-center gap-2 rounded border border-[hsl(var(--border))] text-white px-4 py-2 text-sm hover:bg-[hsl(var(--surface-2))] disabled:opacity-50">
                    <Play className="w-4 h-4" /> Test send
                  </button>
                  <button onClick={deletePlug} disabled={!!busy} className="inline-flex items-center gap-2 rounded border border-red-500/40 text-red-100 px-4 py-2 text-sm hover:bg-red-500/10 disabled:opacity-50">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </div>
              <PlugFields form={editForm} onChange={setEditForm} />
            </>
          ) : (
            <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">
              Select or create a plug message to edit.
            </div>
          )}
        </section>
      </div>

      <section className="surface p-5">
        <div className="label-eyebrow">Rotation preview</div>
        {enabledPlugs.length ? (
          <div className="mt-3 space-y-3">
            <p className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
              {enabledPlugs.length} enabled plug{enabledPlugs.length === 1 ? "" : "s"} selected in a shuffled rotation every {manager.interval_minutes || 15} minutes.
              {manager.min_chat_messages_between_plugs > 0 ? ` At least ${manager.min_chat_messages_between_plugs} chat messages must happen between auto-sends.` : ""}
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {enabledPlugs.map((plug) => (
                <div key={plug.id} className={`rounded border p-4 bg-black/35 ${currentPlug?.id === plug.id ? "border-[hsl(var(--primary))]/70" : "border-[hsl(var(--border))]"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-white truncate">{plug.name}</div>
                    {currentPlug?.id === plug.id && <Pill active>Current</Pill>}
                  </div>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{plug.message}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">No enabled plug is available right now.</p>
        )}
      </section>
    </div>
  );
}

function PlugFields({ form, onChange, compact = false }) {
  const set = (patch) => onChange({ ...form, ...patch });
  return (
    <div className="space-y-4">
      <Field label="Name">
        <input className={inputClass} value={form.name} onChange={(event) => set({ name: event.target.value })} required maxLength={120} />
      </Field>
      <Field label="Message">
        <textarea className={textareaClass} value={form.message} onChange={(event) => set({ message: event.target.value })} required maxLength={500} />
      </Field>
      {!compact && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Toggle label="Enabled" checked={form.enabled} onChange={(enabled) => set({ enabled })} icon={form.enabled ? Eye : EyeOff} />
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label = "", children } = {}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label = "", checked, onChange, icon: Icon } = {}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`flex items-center justify-between gap-3 rounded border px-3 py-2 text-sm ${checked ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-white" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}
    >
      <span>{label}</span>
      <Icon className="w-4 h-4" />
    </button>
  );
}

function Pill({ children, active = false }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${active ? "border-emerald-500/40 text-emerald-100 bg-emerald-500/10" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
      {children}
    </span>
  );
}
